import { useTranslation } from 'react-i18next';
import { Users, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ServiceOption, ProviderOption } from '../../CreateAvailabilityDialog';

interface GroupServiceStepProps {
  services: ServiceOption[]; // already filtered to group services by the wizard hook
  selectedServiceId: number | null;
  onSelectService: (id: number) => void;
  // Admin-only: which provider runs this class. Hidden when not provided.
  providers?: ProviderOption[];
  selectedProviderId?: number | null;
  onProviderIdChange?: (id: number | null) => void;
}

// Group-flow step 2: pick the single group service the rule will run. Lists
// only services where max_capacity > 1; hits an empty state if there are none.
export function GroupServiceStep({
  services,
  selectedServiceId,
  onSelectService,
  providers,
  selectedProviderId,
  onProviderIdChange,
}: GroupServiceStepProps) {
  const { t } = useTranslation('staff');

  if (services.length === 0) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <p className="text-base font-semibold text-amber-900">
            {t('availability.groupClass.servicePicker.empty', 'No group services available')}
          </p>
          <p className="text-sm text-amber-800 mt-1">
            {t(
              'availability.groupClass.servicePicker.emptyHint',
              'Group classes need a service with capacity greater than 1. Create one in Services first.'
            )}
          </p>
        </div>
      </div>
    );
  }

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="p-6 space-y-5">
      {providers && providers.length > 0 && onProviderIdChange && (
        <div className="space-y-2">
          <Label>{t('availability.dialog.staffMember', 'Staff member')}</Label>
          <Select
            value={selectedProviderId?.toString() ?? ''}
            onValueChange={(value) => onProviderIdChange(Number(value))}
          >
            <SelectTrigger className="min-h-[48px]">
              <SelectValue placeholder={t('availability.dialog.selectStaff', 'Select staff member')}>
                {selectedProviderId && providers.find(p => p.id === selectedProviderId) && (
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={providers.find(p => p.id === selectedProviderId)?.avatar_url || undefined} />
                      <AvatarFallback className="text-xs">
                        {getInitials(providers.find(p => p.id === selectedProviderId)?.name || '')}
                      </AvatarFallback>
                    </Avatar>
                    <span>{providers.find(p => p.id === selectedProviderId)?.name}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.id} value={p.id.toString()}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={p.avatar_url || undefined} />
                      <AvatarFallback className="text-xs">{getInitials(p.name)}</AvatarFallback>
                    </Avatar>
                    <span>{p.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label variant="section">
          {t('availability.groupClass.servicePicker.title', 'Choose the group service')}
        </Label>
        <p className="text-xs text-gray-500">
          {t(
            'availability.groupClass.servicePicker.hint',
            'A group class runs one service at a fixed time. Capacity and duration come from the service.'
          )}
        </p>
      </div>

      <div className="space-y-2">
        {services.map((service) => {
          const isSelected = selectedServiceId === service.id;
          return (
            <button
              type="button"
              key={service.id}
              onClick={() => onSelectService(service.id)}
              className={cn(
                'w-full text-left rounded-lg border-2 p-4 transition-all tap-highlight-transparent active:scale-[0.98]',
                'min-h-[64px]',
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300 active:bg-gray-50'
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                    isSelected ? 'bg-blue-100' : 'bg-gray-100'
                  )}
                >
                  <Users className={cn('w-5 h-5', isSelected ? 'text-blue-600' : 'text-gray-500')} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('font-semibold text-base', isSelected ? 'text-blue-900' : 'text-gray-900')}>
                    {service.name}
                  </p>
                  <p className={cn('text-sm mt-0.5', isSelected ? 'text-blue-700' : 'text-gray-500')}>
                    {t('availability.groupClass.servicePicker.spotsLine', '{{capacity}} spots · {{duration}} min', {
                      capacity: service.max_capacity ?? 1,
                      duration: service.duration_minutes ?? 0,
                    })}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
